import { Component, effect, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { InputComponent } from '@app/core/components/input/input.component';
import { PhoneNumberComponent } from '@app/core/components/phone-number/phone-number.component';
import { ButtonComponent } from '@app/core/components/button/button.component';
import { MapBoxComponent } from '@app/core/components/map-box/map-box.component';
import { AboutService } from '@app/+about/shared/services/about.service';
import { RecaptchaService } from '@app/shared/services/recaptcha.service';
import { RecaptchaData } from '@app/shared/types/recaptcha-data.type';
import { PwjToastService } from '@app/core/shared/services/app-toast.service';
import { environment } from '@environments/environment';
import {
  companyLocation,
  contactInfoData,
} from '@app/+about/shared/mock/contact-page';
import { ContactInfo } from '@app/+about/shared/types/contact-info.type';
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';

@Component({
  selector: 'pwj-contact-page',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    TranslateDataPipe,
    NgClass,
    InputComponent,
    PhoneNumberComponent,
    ButtonComponent,
    MapBoxComponent,
  ],
  providers: [AboutService],
  templateUrl: './contact-page.component.html',
  styleUrl: './contact-page.component.scss',
})
export class ContactPageComponent {
  public contactForm!: FormGroup;
  public isDisabledSubmitBtn: boolean = false;
  public readonly contactInfo: ContactInfo[] = contactInfoData;

  public readonly companyLocation = companyLocation;

  private readonly _fb = inject(FormBuilder);
  private readonly _aboutService = inject(AboutService);
  private readonly _recaptchaService = inject(RecaptchaService);
  private readonly _toastService = inject(PwjToastService);

  constructor() {
    this._buildForm();
    this._setupRecaptcha();
  }

  private _buildForm() {
    this.contactForm = this._fb.group({
      name: new FormControl('', Validators.required),
      mobile: new FormControl(null, Validators.required),
      email: new FormControl('', [Validators.required, Validators.email]),
      description: new FormControl('', Validators.required),
    });
  }

  private _setupRecaptcha() {
    this._recaptchaService.recaptchaValidation('contact_us');

    effect(() => {
      if (this._recaptchaService.recaptchaData() !== null) {
        const captchaData =
          this._recaptchaService.recaptchaData() as RecaptchaData;
        this.isDisabledSubmitBtn =
          captchaData?.score < environment.recaptcha.targetScore;
      }
    });
  }

  public submitContactForm() {
    if (!this.contactForm.valid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const formData = this.contactForm.value;

    this._aboutService
      .submitContactFormData({
        name: formData?.name,
        mobile: formData?.mobile,
        email: formData?.email,
        description: formData?.description,
      })
      .subscribe(res => {
        if (!res) {
          return;
        }

        this.contactForm.reset();
        this.contactForm.markAsUntouched();
        Object.keys(this.contactForm.controls).forEach(field => {
          this.contactForm.controls[field].setErrors(null);
        });

        this._toastService.success(
          'toastMessages.messageSubmitted',
          'toastMessages.successTitle'
        );
      });
  }
}
